import React, { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import EditableDrawer from 'components/EditableDrawer';
import SearchSelect from 'components/SearchSelect';
import SingleSelect from 'components/SingleSelect';
import TimePickerFilter from 'components/TimePickerFilter';
import { TmsRouteWaypointType } from '../../route/graphql/queries/getTmsRoutes';
import {
    GET_STATION_LIST_BY_BUSINESS_TYPE,
    GetStationListByBusinessTypeResponse,
} from '../../route/graphql/queries/getStationListByBusinessType';
import { useTmsWaypointTimezones } from '../../hooks/useTmsWaypointTimezones';
import { CREATE_TMS_SCHEDULE, CreateTmsScheduleInput } from '../graphql/mutations/scheduleMutations';
import { useTmsVehicleAndModel } from '../../hooks/useTmsVehicleAndModel';
import { useTmsDriversByRoute } from '../../hooks/useTmsDriversByRoute';
import { useTmsSearch } from '../../hooks/useTmsSearch';
import { useTmsVehiclesByRoute } from '../../hooks/useTmsVehiclesByRoute';
import MultiSelect from 'components/MultiSelect';
import { getWeekdaysOptions, getTmsRouteBusinessTypeLabel } from '../types/TmsScheduleType';
import FilterGroupRow from 'components/FilterGroupRow';
import InputFilterItem from 'components/InputFilterItem';
import {
    WaypointTypeEnum,
    DepartureFrequencyEnum,
    TmsStatusEnum,
    NonFixedOption,
    TmsCarrierSourceTypeEnum,
    TmsBusinessTypeEnum,
    TmsRouteTypeEnum,
    HandoverTypeEnum,
    DepartureDayOffsetEnum,
} from 'enums/TmsEnum';
import {
    convertToTargetTimezone,
    initializeOriginTime,
    calculateWaypointTimes,
    calculateDestinationArrivalTime
} from '../../../../utils/tmsWaypointTimeUtils';
import { showError } from '../../../../utils/exceptionUtils';

interface FilterOptions {
    routeType: string;
    routeId: string | number;
    carrierId: string | number;
    status: string;
    departureFrequency: number[];
    driverId: string | number;
    vehicleId: string | number;
    vehicleModel: string;
    originPlannedDepartureTime: string;
    departureDayOffset: number;
}

interface CreateScheduleDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: () => void;
    statusOptions: { label: string; value: number }[];
    routeTypeOptions: { label: string; value: number }[];
}

// 定义初始状态常量
const initialFilters: FilterOptions = {
    routeType: '',
    routeId: '',
    carrierId: '',
    status: TmsStatusEnum.Active.toString(),
    departureFrequency: [DepartureFrequencyEnum.Monday, DepartureFrequencyEnum.Tuesday, DepartureFrequencyEnum.Wednesday, DepartureFrequencyEnum.Thursday, DepartureFrequencyEnum.Friday, DepartureFrequencyEnum.Saturday, DepartureFrequencyEnum.Sunday],
    driverId: '',
    vehicleId: '',
    vehicleModel: '',
    originPlannedDepartureTime: '',
    departureDayOffset: DepartureDayOffsetEnum.CurrentDay,
};

const CreateScheduleDrawer: React.FC<CreateScheduleDrawerProps> = ({
    visible,
    onClose,
    onSubmit,
    statusOptions,
    routeTypeOptions,
}) => {
    const { t } = useTranslation();
    const drawerTitle = String(t('tms.schedule.drawer.createTitle'));

    const [filters, setFilters] = useState<FilterOptions>(initialFilters);
    const [departureTime, setDepartureTime] = useState<Dayjs | null>(null);
    // 标记 vehicle 是否是从司机查询自动设置的
    const [isVehicleAutoSet, setIsVehicleAutoSet] = useState(false);
    // 记录司机绑定的车辆信息（如果司机有绑定车辆）
    const [driverBoundVehicle, setDriverBoundVehicle] = useState<{ id: number; licensePlate?: string; vehicleNumber?: string } | null>(null);
    // 标记用户是否手动修改过 departureDayOffset
    const [isDayOffsetManuallySet, setIsDayOffsetManuallySet] = useState(false);
    // 用于强制 SearchSelect 重新渲染的 key
    const [routeSelectKey, setRouteSelectKey] = useState(0);

    // 当 drawer 打开时，重置所有状态到初始值
    useEffect(() => {
        if (visible) {
            setFilters({ ...initialFilters });
            setDepartureTime(null);
            setIsVehicleAutoSet(false);
            setDriverBoundVehicle(null);
            setIsDayOffsetManuallySet(false);
            // 更新 key 以强制 SearchSelect 重新渲染并清空选项
            setRouteSelectKey(prev => prev + 1);
        }
    }, [visible]);

    // 从字符串值恢复时间选择器状态
    useEffect(() => {
        if (filters.originPlannedDepartureTime) {
            const time = dayjs(filters.originPlannedDepartureTime, 'HH:mm');
            setDepartureTime(time.isValid() ? time : null);
        } else {
            setDepartureTime(null);
        }
    }, [filters.originPlannedDepartureTime]);

    // 使用 Hook 获取 route waypoints
    const { getRouteWaypoints, fetchRouteWaypoints } = useTmsWaypointTimezones();
    const [waypoints, setWaypoints] = useState<TmsRouteWaypointType[]>([]);

    // 当 routeId 变化时，获取 waypoints
    useEffect(() => {
        if (filters.routeId) {
            const routeId = Number(filters.routeId);
            // 先尝试从缓存获取
            const cached = getRouteWaypoints(routeId);
            if (cached.length > 0) {
                setWaypoints(cached);
            } else {
                // 如果缓存中没有，则查询
                fetchRouteWaypoints(routeId)
                    .then((waypointsData: TmsRouteWaypointType[]) => {
                        setWaypoints(waypointsData);
                    })
            }
        } else {
            setWaypoints([]);
        }
    }, [filters.routeId, getRouteWaypoints, fetchRouteWaypoints]);

    // departureDayOffset 选项
    const departureDayOffsetOptions = useMemo(() => [
        { label: String(t('tms.schedule.drawer.previousDay')), value: DepartureDayOffsetEnum.PreviousDay.toString() },
        { label: String(t('tms.schedule.drawer.currentDay')), value: DepartureDayOffsetEnum.CurrentDay.toString() },
    ], [t]);

    // 使用Hook根据路线获取司机（仅用于获取 driverOptions）
    const { driverOptions: originalDriverOptions } = useTmsDriversByRoute(filters.routeId);

    const driverOptions = useMemo(() => {
        // 仅在已选择路线时提供“非固定”选项，避免初始就出现
        if (filters.routeId) {
            return [
                { label: String(t('tms.schedule.drawer.nonFixedDriver')), value: NonFixedOption.Driver },
                ...originalDriverOptions
            ];
        }
        return originalDriverOptions;
    }, [filters.routeId, originalDriverOptions, t]);

    // 使用Hook进行Route和Schedule搜索
    const { handleRouteSearch } = useTmsSearch();

    // 使用Hook查询车辆和车型信息
    const { getVehicleAndModel, loadingVehicleModel } = useTmsVehicleAndModel({
        onVehicleAndModelFound: (vehicleId, vehicleModelName, _vehicleModelId, vehicle) => {
            // 设置车辆ID
            handleFilterChange('vehicleId', vehicleId.toString());
            // 设置车型名称
            handleFilterChange('vehicleModel', vehicleModelName);
            // 标记为自动设置，禁用 Vehicle 和 Vehicle Model
            setIsVehicleAutoSet(true);
            // 记录司机绑定的车辆信息
            setDriverBoundVehicle(vehicle);
        },
        onVehicleAndModelNotFound: () => {
            // 如果返回 null，说明该司机没有分配车辆，清空车辆和车型，允许手动选择 Vehicle
            handleFilterChange('vehicleId', '');
            handleFilterChange('vehicleModel', '');
            setIsVehicleAutoSet(false);
            // 清空司机绑定的车辆信息
            setDriverBoundVehicle(null);
        },
        onError: (error) => {
            // 出错时也允许手动选择
            handleFilterChange('vehicleId', '');
            handleFilterChange('vehicleModel', '');
            setIsVehicleAutoSet(false);
            // 清空司机绑定的车辆信息
            setDriverBoundVehicle(null);
        }
    });

    // 使用Hook根据routeId自动查询车辆数据
    const { vehiclesData, vehicleOptions: originalVehicleOptions, selectedRoute, carrierIdForQuery } = useTmsVehiclesByRoute(filters.routeId);

    const selectedRouteBusinessType = selectedRoute?.businessType;
    const isExternalStationMode = selectedRouteBusinessType === TmsBusinessTypeEnum.TemuGA || selectedRouteBusinessType === TmsBusinessTypeEnum.TemuSCF;
    const { data: stationData } = useQuery<GetStationListByBusinessTypeResponse>(GET_STATION_LIST_BY_BUSINESS_TYPE, {
        skip: !visible || !isExternalStationMode,
        variables: { businessType: selectedRouteBusinessType },
        fetchPolicy: 'cache-first',
    });
    const stationList = useMemo(
        () => stationData?.getStationListByBusinessType ?? [],
        [stationData?.getStationListByBusinessType]
    );

    const vehicleOptions = useMemo(() => {
        // 仅在已选择路线时提供“非固定”选项
        if (filters.routeId) {
            return [
                { label: String(t('tms.schedule.drawer.nonFixedVehicle')), value: NonFixedOption.Vehicle },
                ...originalVehicleOptions
            ];
        }
        return originalVehicleOptions;
    }, [filters.routeId, originalVehicleOptions, t]);

    // 根据 vehicleId 获取对应的 vehicleModelName
    const selectedVehicleModelName = useMemo(() => {
        if (!filters.vehicleId || filters.vehicleId === NonFixedOption.Vehicle || !vehiclesData?.getTmsVehiclesWithModel) return '';
        const vehicle = vehiclesData.getTmsVehiclesWithModel.find(v => v.id.toString() === filters.vehicleId.toString());
        return vehicle?.vehicleTypeName || '';
    }, [filters.vehicleId, vehiclesData]);

    // 当手动选择 vehicle 时，自动填充 vehicleModel
    useEffect(() => {
        if (!isVehicleAutoSet && filters.vehicleId && filters.vehicleId !== NonFixedOption.Vehicle && selectedVehicleModelName) {
            setFilters(prev => ({ ...prev, vehicleModel: selectedVehicleModelName }));
        } else if (!filters.vehicleId && filters.vehicleModel) {
            // 车辆被清空时，同步清空车型
            setFilters(prev => ({ ...prev, vehicleModel: '' }));
        }
    }, [filters.vehicleId, selectedVehicleModelName, isVehicleAutoSet, filters.vehicleModel]);

    // 根据 vehicleId 获取对应的 vehicleTypeId
    const selectedVehicleTypeId = useMemo(() => {
        if (!filters.vehicleId || filters.vehicleId === NonFixedOption.Vehicle || !vehiclesData?.getTmsVehiclesWithModel) return null;
        const vehicle = vehiclesData.getTmsVehiclesWithModel.find(v => v.id.toString() === filters.vehicleId.toString());
        return vehicle?.vehicleTypeId || null;
    }, [filters.vehicleId, vehiclesData]);

    const vehicleModelDisplayValue = useMemo(() => {
        // 非固定场景
        if (filters.vehicleId === NonFixedOption.Vehicle || filters.vehicleModel === NonFixedOption.Model) {
            return String(t('tms.schedule.drawer.nonFixedVehicleModel'));
        }
        // 已有车型值
        if (filters.vehicleModel) {
            return filters.vehicleModel;
        }
        // 选了具体车辆且已查到车型名称，自动显示
        if (filters.vehicleId && selectedVehicleModelName) {
            return selectedVehicleModelName;
        }
        // 初始未选车辆/车型，保持为空以使用占位提示
        return '';
    }, [filters.vehicleModel, filters.vehicleId, selectedVehicleModelName, t]);

    const isHandoverRoute = selectedRoute?.routeType === TmsRouteTypeEnum.Handover;
    // 交接方类型是否为干线承运商（经外部站点、有终点）；本地尾程商则无终点
    const isLinehaulCarrier = selectedRoute?.carrierSourceType === HandoverTypeEnum.LinehaulCarrier;

    // 交接方类型展示（仅交接路线使用，carrierSourceType 内部/三方已在交接方需求中废弃）
    const handoverCarrierTypeDisplay = useMemo(() => {
        if (!selectedRoute || !isHandoverRoute) return '';
        const st = selectedRoute.carrierSourceType;
        if (st === HandoverTypeEnum.LocalLastMileCarrier) {
            return String(t('tms.route.handoverTypes.localLastMileCarrier'));
        }
        if (st === HandoverTypeEnum.LinehaulCarrier) {
            return String(t('tms.route.handoverTypes.linehaulCarrier'));
        }
        return '-';
    }, [selectedRoute, isHandoverRoute, t]);

    const routeStopsChainDisplay = useMemo(() => {
        if (!waypoints.length) return '';
        const sorted = [...waypoints].sort((a, b) => a.waypointOrder - b.waypointOrder);
        const isExternalStationRoute = selectedRouteBusinessType === TmsBusinessTypeEnum.TemuGA || selectedRouteBusinessType === TmsBusinessTypeEnum.TemuSCF;
        const resolveWaypointDisplayName = (waypoint: TmsRouteWaypointType): string => {
            if (
                isExternalStationRoute &&
                (waypoint.waypointType === WaypointTypeEnum.Waypoint ||
                    waypoint.waypointType === WaypointTypeEnum.Destination)
            ) {
                const extId = waypoint.extWaypointId;
                if (extId != null) {
                    const cfg = stationList.find(c => c.id != null && c.id === extId);
                    const name = (cfg?.sortingCode || '').trim();
                    if (name) {
                        return name;
                    }
                }
                return (waypoint.waypointSortingCode || '').trim() || '-';
            }
            return waypoint.waypointSortingCode || '-';
        };
        return sorted.map(wp => resolveWaypointDisplayName(wp)).join(' → ');
    }, [waypoints, selectedRouteBusinessType, stationList]);

    // 计算 planArrivalTime 和 arrivalDayOffset：根据起点出发时间和 waypoints 配置计算终点到达时间和日期偏移量
    const calculatedPlanArrivalData = useMemo(() => {
        if (!filters.originPlannedDepartureTime || !waypoints || waypoints.length === 0) {
            return null;
        }

        // 按 waypointOrder 排序
        const sortedWaypoints = [...waypoints].sort((a, b) => a.waypointOrder - b.waypointOrder);

        // 获取起点时区offset
        const firstWaypoint = sortedWaypoints[0];
        const baseOffset = firstWaypoint?.waypointTimezoneOffset ?? 0;

        // 所有计算都在起点时区进行
        // 初始化起点时间
        let currentTime = initializeOriginTime(
            filters.originPlannedDepartureTime,
            baseOffset
        );

        // 基准日期（起点的日期，本地时间）
        const baseDate = currentTime.startOf('day');

        // 遍历所有 waypoints，计算每个点的到达和出发时间
        for (let i = 0; i < sortedWaypoints.length; i++) {
            const waypoint = sortedWaypoints[i];
            const wpOffset = waypoint.waypointTimezoneOffset ?? 0;

            if (waypoint.waypointType === WaypointTypeEnum.Origin) {
                // 起点：只有出发时间，没有到达时间
                // currentTime 已经是起点的出发时间（本地时间）
                // 不需要更新 currentTime，继续下一个点
            } else if (waypoint.waypointType === WaypointTypeEnum.Waypoint) {
                // 途经点：所有计算都在起点时区进行
                const { departureTime: waypointDepartureTime } = calculateWaypointTimes(
                    currentTime,
                    waypoint.travelTime || 0,
                    waypoint.dwellTime || 0
                );

                // 更新当前时间点为这个途经点的出发时间（起点时区），用于下一个点的计算
                currentTime = waypointDepartureTime.clone();
            } else if (waypoint.waypointType === WaypointTypeEnum.Destination) {
                // 终点：所有计算都在起点时区进行
                const arrivalTimeInBaseTimezone = calculateDestinationArrivalTime(
                    currentTime,
                    waypoint.travelTime || 0
                );

                // 转换为终点的本地时间用于显示和计算日期偏移
                const arrivalTime = convertToTargetTimezone(
                    arrivalTimeInBaseTimezone,
                    baseOffset,
                    wpOffset
                );

                // 计算日期偏移量：
                // 1. 起点的出发日期（起点时区的本地日期）
                const originDate = baseDate.format('YYYY-MM-DD');

                // 2. 终点的到达日期（终点时区的本地日期）
                const destinationDate = arrivalTime.startOf('day').format('YYYY-MM-DD');

                // 3. 计算日期差：终点日期 - 起点日期
                const originDateObj = dayjs(originDate);
                const destinationDateObj = dayjs(destinationDate);
                const dayDiff = destinationDateObj.diff(originDateObj, 'day');

                // 返回终点的到达时间和日期偏移量
                return {
                    time: arrivalTime.format('HH:mm'),
                    dayOffset: dayDiff
                };
            }
        }

        // 如果没有找到终点，返回 null
        return null;
    }, [filters.originPlannedDepartureTime, filters.routeId, waypoints]);

    // 创建班次的 mutation
    const [createSchedule, { loading: creating }] = useMutation(CREATE_TMS_SCHEDULE);

    const handleFilterChange = (name: keyof FilterOptions, value: string | number | undefined | string[] | number[]) => {
        if (name === 'departureFrequency') {
            const numArray = (value as string[] || []).map(v => Number(v));
            setFilters(prev => ({ ...prev, [name]: numArray }));
        } else {
            setFilters(prev => ({ ...prev, [name]: value || '' }));
        }
    };


    const handleClose = () => {
        onClose();
    };

    const handleSubmit = async () => {
        // 验证必填字段
        if (!filters.routeId) {
            message.error(t('tms.schedule.messages.pleaseSelectRoute'));
            return;
        }
        if (!filters.originPlannedDepartureTime) {
            message.error(t(isHandoverRoute ? 'tms.task.messages.pleaseSelectHandoverTime' : 'tms.schedule.messages.pleaseSelectDepartureTime'));
            return;
        }
        if (!filters.status) {
            message.error(t('tms.schedule.messages.pleaseSelectStatus'));
            return;
        }
        if (filters.departureFrequency.length === 0) {
            message.error(t('tms.schedule.messages.pleaseSelectDepartureFrequency'));
            return;
        }
        if (!isHandoverRoute && !filters.driverId) {
            message.error(t('tms.schedule.messages.pleaseSelectDriver'));
            return;
        }
        if (!isHandoverRoute && filters.driverId !== NonFixedOption.Driver && !filters.vehicleId) {
            message.error(t('tms.schedule.messages.pleaseSelectVehicle'));
            return;
        }
        try {
            // 计算 planArrivalTime 和 arrivalDayOffset：
            // 交接路线中，本地尾程商无终点 → 到达时间为 null；干线承运商经外部站点有终点 → 按途经点配置算出到达时间
            // （以 carrierSourceType 判定，而非 businessType：TemuGA 也可选本地尾程商，此时同样无终点）  本地尾程商-无终点
            const hasDestination = !isHandoverRoute || isLinehaulCarrier;
            const planArrivalTime = hasDestination ? (calculatedPlanArrivalData?.time || filters.originPlannedDepartureTime) : null;
            const arrivalDayOffset = hasDestination ? (calculatedPlanArrivalData?.dayOffset ?? 0) : null;

            // 从 route 获取 startWarehouseId 作为 warehouseId
            const warehouseId = selectedRoute?.startWarehouseId 
                ? Number(selectedRoute.startWarehouseId) 
                : 0;
            
            if (!warehouseId) {
                message.error(t('tms.schedule.messages.routeStartWarehouseIdMissing'));
                return;
            }

            // 构建输入对象
            const input: CreateTmsScheduleInput = {
                scheduleName: selectedRoute?.routeName
                    ? `${selectedRoute.routeName}-${filters.originPlannedDepartureTime}`
                    : `Schedule-${filters.originPlannedDepartureTime}`,
                routeId: Number(filters.routeId),
                planDepartureTime: filters.originPlannedDepartureTime, // HH:mm 格式
                planArrivalTime: planArrivalTime, // 根据 waypoints 配置计算得出
                arrivalDayOffset: arrivalDayOffset, // 根据 waypoints 配置计算得出的日期偏移量
                departureDayOffset: Number(filters.departureDayOffset), // 出发日归属：-1=前一日，0=当日
                departureFrequency: JSON.stringify(filters.departureFrequency.sort()), // 转换为 JSON 字符串
                carrierId: carrierIdForQuery || Number(selectedRoute?.carrierId) || 0,
                driverId: filters.driverId === NonFixedOption.Driver ? null : (filters.driverId ? Number(filters.driverId) : null), // AccountTmsDriver 的 id
                vehicleId: filters.vehicleId === NonFixedOption.Vehicle ? null : (filters.vehicleId ? Number(filters.vehicleId) : null),
                vehicleTypeId: filters.vehicleModel === NonFixedOption.Model ? null : (selectedVehicleTypeId ? Number(selectedVehicleTypeId) : null),
                warehouseId: warehouseId, // 使用 route 的 startWarehouseId
                status: Number(filters.status),
            };

            await createSchedule({
                variables: { input },
            });

            message.success(t('tms.schedule.messages.scheduleCreatedSuccessfully'));
            // 先调用父组件的回调刷新列表，等待完成后再关闭抽屉
            await onSubmit(); // 调用父组件的回调，可能用于刷新列表
            handleClose(); // 关闭抽屉
        } catch (error: any) {
            console.error('Failed to create schedule:', error);
            const hasMessageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
            hasMessageKey ? showError(error) : message.error(error?.message || t('tms.schedule.messages.failedToCreateSchedule'));
        }
    };

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={handleClose}
            onSubmit={handleSubmit}
            confirmDisabled={creating}
            confirmLoading={creating}
            width={800}
        >
            <div className="bg-white mb-4">
                <div className="flex gap-4">
                    <div className="flex-1">
                        <div className="mb-4">
                            <SearchSelect
                                key={`route-select-${routeSelectKey}`}
                                label={String(t('tms.schedule.drawer.route'))}
                                placeholder={String(t('tms.schedule.drawer.searchRoute'))}
                                value={filters.routeId}
                                onChange={(value) => {
                                    handleFilterChange('routeId', value);
                                    handleFilterChange('driverId', '');
                                    // 切换路线时，清空 vehicle 和 vehicle model，vehicle 变为可选择
                                    handleFilterChange('vehicleId', '');
                                    handleFilterChange('vehicleModel', '');
                                    setIsVehicleAutoSet(false);
                                    setDriverBoundVehicle(null);
                                }}
                                onSearch={(searchValue, callback) => handleRouteSearch(searchValue, callback, TmsStatusEnum.Active)}
                                spanClassName="w-[7em]"
                                required
                                forceHorizontal
                            />
                        </div>
                        <div className="mb-4">
                            <InputFilterItem
                                label={String(t('tms.schedule.drawer.routeBusinessType'))}
                                placeholder={String(t('tms.schedule.drawer.autoFilledFromRoute'))}
                                value={getTmsRouteBusinessTypeLabel(t, selectedRoute?.businessType)}
                                onChange={() => {}}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal
                            />
                        </div>
                        {isHandoverRoute && (
                        <div className="mb-4">
                            <InputFilterItem
                                label={String(t('tms.schedule.drawer.handoverCarrierType'))}
                                placeholder={String(t('tms.schedule.drawer.autoFilledFromRoute'))}
                                value={handoverCarrierTypeDisplay}
                                onChange={() => {}}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal
                            />
                        </div>
                        )}
                        <div className="mb-4">
                            <InputFilterItem
                                label={String(t(isHandoverRoute ? 'tms.schedule.drawer.handoverCarrier' : 'tms.schedule.drawer.carrier'))}
                                placeholder={String(t('tms.schedule.drawer.autoFilledFromRoute'))}
                                value={selectedRoute?.carrierName ? String(selectedRoute.carrierName) : ''}
                                onChange={() => {}}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal
                            />
                        </div>
                        {waypoints.length > 0 && (
                            <div className="mb-4">
                                <InputFilterItem
                                    label={String(t('tms.schedule.drawer.waypoint'))}
                                    placeholder=""
                                    value={routeStopsChainDisplay}
                                    onChange={() => {}}
                                    spanClassName="w-[7em]"
                                    disabled
                                    forceHorizontal
                                />
                            </div>
                        )}
                        <div className="mb-4">
                            <FilterGroupRow
                                label={String(t(isHandoverRoute ? 'tms.schedule.drawer.handoverTime' : 'tms.schedule.drawer.departureTime'))}
                                spanClassName="w-[7em]"
                                required
                                forceHorizontal
                                childWidths={['70%', '30%']}
                            >
                                <TimePickerFilter
                                    value={departureTime}
                                    onChange={(time) => {
                                        setDepartureTime(time);
                                        handleFilterChange('originPlannedDepartureTime', time ? time.format('HH:mm') : '');
                                        // 首次选择出发时间时，自动计算 departureDayOffset 默认值
                                        if (time && !isDayOffsetManuallySet) {
                                            const hour = time.hour();
                                            const minute = time.minute();
                                            const totalMinutes = hour * 60 + minute;
                                            // 00:00–06:00（含边界）→ 前一日，06:00之后 → 当日
                                            const offset = totalMinutes <= 360
                                                ? DepartureDayOffsetEnum.PreviousDay
                                                : DepartureDayOffsetEnum.CurrentDay;
                                            handleFilterChange('departureDayOffset', offset.toString());
                                        }
                                    }}
                                    format="HH:mm"
                                    placeholder={String(t(isHandoverRoute ? 'tms.schedule.drawer.selectHandoverTime' : 'tms.schedule.drawer.selectDepartureTime'))}
                                    minuteStep={5}
                                    showNow={false}
                                />
                                <SingleSelect
                                    options={departureDayOffsetOptions}
                                    value={filters.departureDayOffset.toString()}
                                    onChange={(value) => {
                                        handleFilterChange('departureDayOffset', value);
                                        setIsDayOffsetManuallySet(true);
                                    }}
                                    allowClear={false}
                                />
                            </FilterGroupRow>
                        </div>
                        <div className="mb-4">
                            <SingleSelect
                                label={String(t('tms.schedule.drawer.status'))}
                                options={statusOptions}
                                value={filters.status}
                                onChange={value => handleFilterChange('status', value)}
                                spanClassName="w-[7em]"
                                required
                                forceHorizontal
                            />
                        </div>
                        <div className="mb-4">
                            <MultiSelect
                                label={String(t('tms.schedule.drawer.departureFrequency'))}
                                options={getWeekdaysOptions(t)}
                                value={filters.departureFrequency.map(v => v.toString())}
                                onChange={value => handleFilterChange('departureFrequency', value)}
                                spanClassName="w-[7em]"
                                required
                                forceHorizontal
                            />
                        </div>
                        <div className="mb-4">
                            <SingleSelect
                                label={String(t('tms.schedule.drawer.driver'))}
                                placeholder={String(t('tms.schedule.drawer.selectDriver'))}
                                options={driverOptions}
                                value={filters.driverId ? filters.driverId.toString() : ''}
                                onChange={(value) => {
                                    handleFilterChange('driverId', value);
                                    // 清空车辆和车型，等待查询结果
                                    handleFilterChange('vehicleId', '');
                                    handleFilterChange('vehicleModel', '');
                                    setIsVehicleAutoSet(false);
                                    setDriverBoundVehicle(null);
                                    // 如果选择了司机，调用查询获取车辆和车型信息
                                    if (value === NonFixedOption.Driver) {
                                        handleFilterChange('vehicleId', NonFixedOption.Vehicle);
                                        handleFilterChange('vehicleModel', NonFixedOption.Model);
                                    } else if (value) {
                                        getVehicleAndModel({
                                            variables: { driverId: parseInt(value.toString()) }
                                        });
                                    }
                                }}
                                spanClassName="w-[7em]"
                                required={!isHandoverRoute}
                                forceHorizontal
                            />
                        </div>
                        <div className="mb-4">
                            <SingleSelect
                                label={String(t('tms.schedule.drawer.vehicle'))}
                                placeholder={String(t('tms.schedule.drawer.selectVehicle'))}
                                options={vehicleOptions}
                                value={filters.vehicleId ? filters.vehicleId.toString() : ''}
                                onChange={(value) => {
                                    handleFilterChange('vehicleId', value);
                                    if (value === NonFixedOption.Vehicle) {
                                        handleFilterChange('vehicleModel', NonFixedOption.Model);
                                    }
                                    // 手动选择 vehicle 时，标记为非自动设置
                                    setIsVehicleAutoSet(false);
                                }}
                                spanClassName="w-[7em]"
                                required={!isHandoverRoute && filters.driverId !== NonFixedOption.Driver}
                                forceHorizontal
                            />
                        </div>
                        <div className="mb-4">
                            <InputFilterItem
                                label={String(t('tms.schedule.drawer.vehicleModel'))}
                                placeholder={String(t('tms.schedule.drawer.autoFilledFromVehicle'))}
                                value={vehicleModelDisplayValue}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal
                            />
                        </div>
                    </div>
                </div>
            </div>
        </EditableDrawer>
    );
};

export default CreateScheduleDrawer;